import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Delete,
  Edit,
  Check,
  CloudDone,
  CloudOff,
  HourglassEmpty,
  AccessTime,
  Star
} from '@mui/icons-material';
import { api } from '../../utils/api';

const SubtaskList = ({ subtasks, onSubtaskUpdated, onSubtaskDeleted, readOnly = false }) => {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEdit = (subtask) => {
    setEditingId(subtask.id);
    setEditTitle(subtask.title);
    setEditDescription(subtask.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
  };

  const handleSaveEdit = async (subtaskId) => {
    if (!editTitle.trim()) return;

    setLoading(true);
    try {
      const response = await api.put(`/subtasks/${subtaskId}`, {
        title: editTitle.trim(),
        description: editDescription.trim() || null
      });
      onSubtaskUpdated(response.data);
      handleCancelEdit();
    } catch (err) {
      console.error('Failed to update subtask:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subtaskId) => {
    if (!window.confirm('Are you sure you want to delete this subtask?')) return;

    setLoading(true);
    try {
      await api.delete(`/subtasks/${subtaskId}`);
      onSubtaskDeleted(subtaskId);
    } catch (err) {
      console.error('Failed to delete subtask:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSyncStatusIcon = (status) => {
    switch (status) {
      case 'synced':
        return <Tooltip title="Synced to Jira"><CloudDone color="success" fontSize="small" /></Tooltip>;
      case 'failed':
        return <Tooltip title="Sync failed"><CloudOff color="error" fontSize="small" /></Tooltip>;
      default:
        return <Tooltip title="Pending sync"><HourglassEmpty color="action" fontSize="small" /></Tooltip>;
    }
  };

  const getEstimateDisplay = (subtask) => {
    if (subtask.estimateType === 'none' || !subtask.estimate) return null;

    const icon = subtask.estimateType === 'hours' ? <AccessTime fontSize="small" /> : <Star fontSize="small" />;
    const label = subtask.estimateType === 'hours' ? `${subtask.estimate}h` : `${subtask.estimate} pts`;

    return (
      <Chip
        icon={icon}
        label={label}
        size="small"
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  };

  if (!subtasks || subtasks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pl: 2, py: 1 }}>
        No subtasks
      </Typography>
    );
  }

  return (
    <List dense sx={{ pl: 2 }}>
      {subtasks.map((subtask) => (
        <ListItem
          key={subtask.id}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            mb: 0.5,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {editingId === subtask.id ? (
            <Box sx={{ width: '100%' }}>
              <TextField
                size="small"
                fullWidth
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Subtask title"
                sx={{ mb: 1 }}
                autoFocus
              />
              <TextField
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Description (optional)"
                sx={{ mb: 1 }}
              />
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSaveEdit(subtask.id)}
                  disabled={loading || !editTitle.trim()}
                  startIcon={loading ? <CircularProgress size={16} /> : <Check />}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2">
                      {subtask.title}
                    </Typography>
                    {getEstimateDisplay(subtask)}
                    {subtask.jiraKey && (
                      <Chip
                        label={subtask.jiraKey}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        component="a"
                        href={subtask.jiraUrl}
                        target="_blank"
                        clickable
                      />
                    )}
                  </Box>
                }
                secondary={subtask.description}
              />
              <ListItemSecondaryAction>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getSyncStatusIcon(subtask.syncStatus)}
                  {!readOnly && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(subtask)}
                        disabled={loading}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(subtask.id)}
                        disabled={loading}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </ListItemSecondaryAction>
            </>
          )}
        </ListItem>
      ))}
    </List>
  );
};

export default SubtaskList;
